<!-- GradeNumberChart.vue -->
<template>
  <div ref="chartRef" class="chart"></div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import * as echarts from 'echarts';

const chartRef = ref(null);
let chart = null;

/* ---------- 数据：直接复用 Student.vue 里的 studentData ---------- */
const studentData = {
  kindergarten: [
    63733, 63281, 65106, 63603, 64021, 71959, 77429, 79412, 71032, 62393, 52539, 48136, 48268,
    47495, 46166, 43864, 42845, 41846, 40868, 40378, 40373,
  ],
  primarySchool: [
    154082, 148184, 143946, 142540, 142679, 142944, 142095, 141907, 148043, 149857, 155609, 153338,
    149707, 143566, 129907, 120522, 112812, 107359, 104654, 103431, 102311,
  ],
  juniorMiddle: [
    89489, 85639, 82621, 78066, 73836, 69837, 67488, 66759, 66762, 67975, 70614, 72088, 71193,
    71271, 77357, 81415, 82751, 74394, 66705, 59228, 53925,
  ],
  seniorHigh: [
    38397, 38248, 38513, 38942, 39277, 40761, 40436, 40215, 39740, 39256, 41517, 43648, 45094,
    46822, 46933, 47679, 52544, 56019, 57650, 52406, 47289,
  ],
};
const years = [
  '2015',
  '2016',
  '2017',
  '2018',
  '2019',
  '2020',
  '2021',
  '222',
  '2023',
  '2024',
  '2025',
  '2026',
  '2027',
  '2028',
  '2029',
  '2030',
  '2031',
  '2032',
  '2033',
  '2034',
  '2035',
];

/* ---------- 数值格式化 ---------- */
const format = (v) => (v >= 10000 ? (v / 10000).toFixed(1) + '万' : v.toString());

/* ---------- option：完全复刻 studentChartOption ---------- */
const option = computed(() => ({
  backgroundColor: 'transparent',
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'cross', label: { backgroundColor: '#6a7985' } },
    formatter(params) {
      let res = params[0].axisValue + '<br/>';
      params.forEach((p) => {
        res += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${p.color}"></span>`;
        res += `${p.seriesName}: ${format(p.value)}<br/>`;
      });
      return res;
    },
  },
  legend: {
    data: ['幼儿园在园数', '小学在校生数', '初中在校生数', '高中在校生数'],
    top: 0,
    textStyle: { fontSize: 11 },
  },
  grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: years,
    axisLabel: { interval: 0, rotate: 45, fontSize: 11 },
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: (v) => (v >= 10000 ? v / 10000 + '万' : v),
      fontSize: 11,
    },
  },
  series: [
    {
      name: '幼儿园在园数',
      type: 'line',
      stack: 'Total',
      data: studentData.kindergarten,
      itemStyle: { color: '#91cc75' },
      label: {
        show: true,
        position: 'top',
        formatter: (p) => format(p.value),
        fontSize: 10,
        color: '#91cc75',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(145,204,117,0.4)' },
            { offset: 1, color: 'rgba(145,204,117,0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '小学在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.primarySchool,
      itemStyle: { color: '#fac858' },
      label: {
        show: true,
        position: 'top',
        formatter: (p) => format(p.value),
        fontSize: 10,
        color: '#fac858',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(250,200,88,0.4)' },
            { offset: 1, color: 'rgba(250,200,88,0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '初中在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.juniorMiddle,
      itemStyle: { color: '#ee6666' },
      label: {
        show: true,
        position: 'top',
        formatter: (p) => format(p.value),
        fontSize: 10,
        color: '#ee6666',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(238,102,102,0.4)' },
            { offset: 1, color: 'rgba(238,102,102,0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '高中在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.seniorHigh,
      itemStyle: { color: '#73c0de' },
      label: {
        show: true,
        position: 'top',
        formatter: (p) => format(p.value),
        fontSize: 10,
        color: '#73c0de',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(115,192,222,0.4)' },
            { offset: 1, color: 'rgba(115,192,222,0.1)' },
          ],
        },
      },
      smooth: true,
    },
  ],
}));

/* ---------- 初始化 & 响应式 ---------- */
function initChart() {
  chart = echarts.init(chartRef.value);
  chart.setOption(option.value);
  window.addEventListener('resize', () => chart?.resize());
}

onMounted(() => initChart());
</script>

<style scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
